import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import type { IconButtonProps } from '@mui/material/IconButton';
import IconButton from '@mui/material/IconButton';
import MenuItem, { menuItemClasses } from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { getMediaUrl, getSession, logout, useGetAllProfileQuery } from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { usePathname } from 'src/components/use-pathname';

// ----------------------------------------------------------------------

export type AccountPopoverProps = IconButtonProps & {
  data?: Array<{
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
    onClick?: () => void;
  }>;
};

export function AccountPopover({ data = [], sx, ...other }: AccountPopoverProps) {
  const token = getSession();
  const { data: profileData } = useGetAllProfileQuery({ token });
  const navigate = useNavigate();

  const pathname = usePathname();

  const [openPopover, setOpenPopover] = useState<HTMLButtonElement | null>(null);

  const handleOpenPopover = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenPopover(event.currentTarget);
  }, []);

  const handleClosePopover = useCallback(() => {
    setOpenPopover(null);
  }, []);

  const handleClickItem = useCallback(
    (option: { href: string; onClick?: () => void }) => {
      handleClosePopover();
      if (option.onClick) {
        option.onClick();
      } else {
        navigate({ to: option.href });
      }
    },
    [handleClosePopover, navigate],
  );

  const handleLogout = useCallback(async () => {
    await logout();
    navigate({ to: '/auth/login' });
  }, [navigate]);

  const name = `${profileData?.firstName} ${profileData?.lastName}`;

  return (
    <>
      <IconButton
        onClick={handleOpenPopover}
        sx={{
          ml: { xs: 0.5, md: 0.8 },
          p: '1px',
          width: { xs: 25, md: 27.1 },
          height: { xs: 25, md: 27.1 },
          background: theme =>
            `conic-gradient(${theme.palette.primary.light}, ${theme.palette.warning.light}, ${theme.palette.primary.light})`,
          ...sx,
        }}
        {...other}
      >
        <Avatar
          src={getMediaUrl(profileData?.avatar)}
          alt={name}
          sx={{
            width: 1,
            height: 1,
          }}
        >
          {name.charAt(0).toUpperCase()}
        </Avatar>
      </IconButton>

      <Popover
        open={!!openPopover}
        anchorEl={openPopover}
        onClose={handleClosePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: { width: 200 },
          },
        }}
      >
        <Box sx={{ p: 2, pb: 1.5 }}>
          <Typography variant="subtitle2" noWrap>
            {name}
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {profileData?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuList
          disablePadding
          sx={{
            p: 1,
            gap: 0.5,
            display: 'flex',
            flexDirection: 'column',
            [`& .${menuItemClasses.root}`]: {
              px: 1,
              gap: 2,
              borderRadius: 0.75,
              color: 'text.secondary',
              '&:hover': { color: 'text.primary' },
              [`&.${menuItemClasses.selected}`]: {
                color: 'text.primary',
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightSemiBold',
              },
            },
          }}
        >
          {data.map(option => (
            <MenuItem key={option.label} selected={option.href === pathname} onClick={() => handleClickItem(option)}>
              {option.icon}
              {option.label}
            </MenuItem>
          ))}
        </MenuList>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button
            onClick={handleLogout}
            fullWidth
            color="error"
            size="medium"
            variant="text"
            sx={{
              color: theme => `${theme.palette.error.main} !important`,
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            Logout
          </Button>
        </Box>
      </Popover>
    </>
  );
}
